import { Col, Row } from "react-bootstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Typeahead } from "react-bootstrap-typeahead";

import { FormattedMessage, useIntl } from "react-intl";

import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createAdditionalCost } from "api";
import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import errorToast from "helpers/errorToast";
import Modal from "components/v2/Modal/Modal";
import Button from "components/v2/Button/Button";
import ChoiceChip from "components/v2/ChoiceChip/ChoiceChip";
import Input from "components/v2/Input/Input";
import "./AdditionalCostDialog.scss";

enum AdditionalCostType {
  ADDITIONAL_COST = 1,
  DISCOUNT = 2,
}

enum ChargeType {
  ORIGIN_CHARGE = 1,
  FREIGHT_CHARGE = 2,
  DESTINATION_CHARGE = 3,
}

interface AdditionalCostFormValue {
  cost_type: AdditionalCostType;
  description: string;
  amount: number;
  currency_code: string;
  comment: string;
  charge_type?: ChargeType;
}

interface AdditionalCostDialogProps {
  isOpen: boolean;
  close: () => void;
  expectedAmountDueCurrency: string;
}

export default function AdditionalCostDialogV2({ isOpen, close, expectedAmountDueCurrency }: AdditionalCostDialogProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const id = useParams().id!.toUpperCase();

  const methods = useForm<AdditionalCostFormValue>({
    defaultValues: {
      cost_type: AdditionalCostType.ADDITIONAL_COST,
      description: "",
      amount: undefined,
      currency_code: expectedAmountDueCurrency,
      comment: "",
      charge_type: 1,
    },
  });

  const { mutate: createCostMutation } = useMutation({
    mutationFn: (additionalCost: AdditionalCostFormValue) =>
      createAdditionalCost({
        shipment_reference: id,
        ...additionalCost,
      }),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "tracking.components.additionalCostDialog.createSuccess",
          defaultMessage: "Additional cost successfully added",
        }),
        { type: "success" },
      );
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(id) });
      close();
      methods.reset();
    },
    onError: () =>
      errorToast(
        intl.formatMessage({
          id: "tracking.components.additionalCostDialog.createFail",
          defaultMessage: "Fail to add additional cost",
        }),
      ),
  });

  const { data: currencies = [] } = useGetCurrencies();
  const currencyOptions = currencies.map((currency) => ({
    key: currency.id,
    label: currency.code,
    name: currency.name,
    value: currency.code,
    code: currency.code,
  }));

  const additionalCostOptions = [
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.additionalCost",
        defaultMessage: "Additional cost",
      }),
      value: AdditionalCostType.ADDITIONAL_COST.toString(),
    },
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.discount",
        defaultMessage: "Discount",
      }),
      value: AdditionalCostType.DISCOUNT.toString(),
    },
  ];

  const chargeCategoryOptions = [
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.freightCharge",
        defaultMessage: "Freight charge",
      }),
      value: ChargeType.FREIGHT_CHARGE.toString(),
    },
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.originCharge",
        defaultMessage: "Origin charge",
      }),
      value: ChargeType.ORIGIN_CHARGE.toString(),
    },
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.destinationCharge",
        defaultMessage: "Destination charge",
      }),
      value: ChargeType.DESTINATION_CHARGE.toString(),
    },
  ];

  const submitAdditionalCost = (values: AdditionalCostFormValue) => {
    createCostMutation(values);
  };

  const handleCostType = (value: string) => {
    methods.setValue("cost_type", Number(value));
  };

  const currencyFieldOptions = currencyOptions.map((item) => ({
    label: `${item.code} ${item.name}`,
    value: item.code,
  }));

  return (
    <Modal show={isOpen} onHide={close} size="xl">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">
          {intl.formatMessage({
            id: "tracking.components.additionalCostDialog.addCost",
            defaultMessage: "Add cost",
          })}
        </div>
        <div className="text-tertiary text-sm mb-2xl">
          <FormattedMessage
            id="tracking.components.additionalCostDialog.subTitle"
            defaultMessage="Add an additional charge or discount item"
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          <form id="submit-pod-form">
            <div className="mt-sm">
              <Row>
                <Col>
                  <div className="text-sm text-secondary-v2 font-medium mb-sm">
                    <FormattedMessage id="tracking.components.additionalCostDialog.costType" defaultMessage="Cost type" />
                  </div>

                  <ChoiceChip
                    options={additionalCostOptions}
                    onChange={(value: any) => handleCostType(value)}
                    selected={AdditionalCostType.ADDITIONAL_COST.toString()}
                  />
                </Col>
              </Row>
              <Row className="mt-3xl">
                <Col>
                  <div className="text-sm text-secondary-v2 font-medium mb-sm">
                    <FormattedMessage
                      id="tracking.components.additionalCostDialog.chargeCategory"
                      defaultMessage="Charge category"
                    />
                  </div>
                  <ChoiceChip
                    options={chargeCategoryOptions}
                    onChange={(value: any) => methods.setValue("charge_type", Number(value))}
                    selected="1"
                  />
                </Col>
              </Row>
              <Row className="mt-3xl">
                <Col>
                  <Input
                    label={intl.formatMessage({
                      id: "general.description",
                      defaultMessage: "Description",
                    })}
                    name="description"
                    required
                  />
                </Col>
              </Row>
              <Row className="mt-3xl">
                <Col md="6">
                  <Input
                    label={intl.formatMessage({
                      id: "general.amount",
                      defaultMessage: "Amount",
                    })}
                    name="amount"
                    type="number"
                    required
                  />
                </Col>
                <Col md="6">
                  <div className="pod-container__timezone-container">
                    <div className="text-sm text-secondary-v2 font-medium mb-sm">
                      <FormattedMessage id="general.currency" defaultMessage="Currency" />
                      <span className="text-danger ml-1">*</span>
                    </div>

                    <Typeahead
                      onChange={(value: any) => methods.setValue("currency_code", value[0]?.value)}
                      options={currencyFieldOptions}
                      placeholder="Search currency..."
                      id="currency_code"
                      defaultInputValue={expectedAmountDueCurrency}
                    />
                  </div>
                </Col>
              </Row>

              <Row className="mt-3xl">
                <Col>
                  <div className="text-sm text-secondary-v2 font-medium mb-sm">
                    <FormattedMessage
                      id="tracking.components.additionalCostDialog.additionalComments"
                      defaultMessage="Additional comments (optional)"
                    />
                  </div>
                  <div>
                    <Controller
                      name="comment"
                      control={methods.control}
                      render={({ field: { onChange, value, ...rest }, fieldState: { error } }) => (
                        <>
                          <div className="additional-comments-textarea">
                            <textarea {...rest} value={value || ""} onChange={(e) => onChange(e.target.value)} />
                          </div>
                          {error && <span className="text-sm error-text text-error-primary">{error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              label={intl.formatMessage({ id: "general.back", defaultMessage: "Back" })}
              onClick={close}
              variant="secondary-gray"
            />
          </div>
          <Button
            label={intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" })}
            disabled={false}
            onClick={methods.handleSubmit(submitAdditionalCost)}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
